import {
  GameState,
  GameStatus,
  GameTypes,
  NewGameTypes,
  MultiChoiceQuestionPart,
  QuizQuestionResult,
} from '@/types/game';
import { Question } from '@/types/question';
import { Quiz } from '@/types/quiz';
import { Topic, Unit } from '@/types/units';
import commonUtils from './common';

function getTopicProgress(
  progress: any,
  unitId: number,
  topicId: number
): { completedActivitiesCount?: number } | undefined {
  const map = progress?.unit?.topicsOverviewPerUnit;
  if (map == null) return undefined;
  const byUnit = map[unitId] ?? map[String(unitId)];
  if (byUnit == null) return undefined;
  return byUnit[topicId] ?? byUnit[String(topicId)];
}

const GameUtils = {
  // Returns true if the timer should be running based on game status
  shouldHandleTimer: (game: GameState): boolean =>
    !game.showPreQuiz &&
    game.gameType !== GameTypes.Practice &&
    game.gameStatus !== GameStatus.PAUSED &&
    game.soundWaveIsReady !== false &&
    game.gameStatus !== GameStatus.PAUSED_SIMPLE &&
    game.gameStatus !== GameStatus.CHECK_ANSWER &&
    game.gameStatus !== GameStatus.QUESTION_ANSWERED &&
    game.gameStatus !== GameStatus.SHOW_HINTS &&
    game.gameStatus !== GameStatus.SHOW_DICTIOANRY &&
    game.gameStatus !== GameStatus.TIME_ENDED &&
    game.gameStatus !== GameStatus.ENDED &&
    game.gameStatus !== GameStatus.DICTIONARY_OPEN &&
    game.gameStatus !== GameStatus.OPEN_WRITING_AI_REVIEW &&
    !game.isLevelTest &&
    !game.isFinalAssessment &&
    !game.showOverlay &&
    !game.pauseTimer,

  getSecondsBetweenDates: (startDate: number, endDate: number): number => {
    return Math.trunc((endDate - startDate) / 1000);
  },
  // Returns the amount of total questions
  totalQuestions: (gameQuestions: Question[]): number => gameQuestions.length - 1,

  // Returns the current question by index
  currentQuestion: (game: GameState): Question => game.gameQuestions[game.currentQuestionIndex],

  // Returns a formatted string of the current remaining time
  gameTimeString: (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  },

  // Verify selected answer by game type, each game type might have different logic
  verifyAnswerByGameType: (game: GameState): boolean => {
    let levelTestReadCurrentReadQuestion;
    const isLevelTest = game.isLevelTest;
    if (isLevelTest) {
      levelTestReadCurrentReadQuestion = game.gameQuestions[game.currentReadQuestionIndex];
    }

    if (
      game.gameType === GameTypes.Pronunciation ||
      game.gameType === NewGameTypes.Speak ||
      (game.gameType === NewGameTypes.NaturalConversation &&
        game.gameQuestions[game.currentQuestionIndex]?.newData?.data?.metadata?.skillId ===
          GameTypes.Pronunciation)
    ) {
      const currentQuestion = game.gameQuestions[game.currentQuestionIndex];

      if (game.pickedAnswer) {
        const lowerCaseQuestion = currentQuestion.question?.toLowerCase();
        const lowerCasePickedAnswer = game.pickedAnswer?.toLowerCase();

        const isSimilar = commonUtils.isSimilarPhrases(lowerCaseQuestion, lowerCasePickedAnswer);

        return isSimilar;
      }

      return currentQuestion.question?.toLowerCase() === game.pickedAnswer?.toLowerCase();
    } else {
      return isLevelTest
        ? levelTestReadCurrentReadQuestion?.answer === game.pickedAnswer
        : //FIXME: Need to use correct values for ClosedSpelling answer game type
          game.gameType !== GameTypes.ClosedSpelling
          ? game.gameQuestions[game.currentQuestionIndex].answer === game.pickedAnswer
          : game.gameQuestions[game.currentQuestionIndex].answer.join(',') === game.pickedAnswer;
    }
  },

  // Returns true if there is one question left
  isOneQuestionLeft: (currentIndex: number, gameQuestions: Question[]) => {
    // Check if we're past the last question
    if (currentIndex >= gameQuestions.length) {
      return false; // No questions left
    }
    const total = GameUtils.totalQuestions(gameQuestions);
    return total - currentIndex <= 1;
  },

  // Returns a number if the display focus CARD after X per quiz is not null
  displayFocusAfterX: (quiz: Quiz | Question): number | null => {
    return quiz?.cardsLogic?.displayFocusCardAfterXQuestions ?? null;
  },

  // Returns a number if the display keep going CARD after X per quiz is not null
  displayKeepGoingAfterX: (quiz: Quiz | Question): number | null => {
    return quiz?.cardsLogic?.displayKeepGoingCardAfterXQuestions ?? null;
  },

  // Suffle the distractors along with the answer
  getPossibleAnswers: (question: Question | null): string[] => {
    if (!question) {
      return [];
    }
    const gameType = question.gameType as NewGameTypes | GameTypes;
    const answer = question.answer;

    const answers: string[] = [...question.distractors];

    let possibleAnswers: string[] = [];
    if (gameType === GameTypes.ClosedSpelling) {
      answers.push(...answer);
      possibleAnswers = answers;
    } else {
      answers.push(answer as string);
      possibleAnswers = answers;
    }

    return commonUtils.shuffleArray(possibleAnswers);
  },

  // Split question by reply
  getMultiChoiceQuestionParts: (question: Question): MultiChoiceQuestionPart[] => {
    let blankIndex = 0;
    const answer = question.answer;

    let splittedAnswer: string[] = [];

    const answerText = typeof answer === 'string' ? answer : answer?.text ?? '';

    const trimmed = answerText.trim();

    if (trimmed.includes(' / ')) {
      // Multiple words per blank using preffered ` / ` separator, like 'always gets / never does'
      splittedAnswer = trimmed.split(' / ').map((a: string) => a.trim());
    } else {
      // Fallback to detect possible delimiters like 'always gets - never does' or 'always gets | never does'
      const possibleDelimiters = ['/', '|', ';', ',', ' - ', ' — '];
      for (const delimiter of possibleDelimiters) {
        if (trimmed.includes(delimiter)) {
          const regex =
            delimiter === '/'
              ? /\s*\/\s*/
              : new RegExp(`\\s*${delimiter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`);
          splittedAnswer = trimmed.split(regex).map((a: string) => a.trim());
          break;
        }
      }

      // Final fallback to whitespace if no delimiters matched like 'gets does'
      if (splittedAnswer.length === 0) {
        splittedAnswer = trimmed.split(/\s+/);
      }
    }

    const splittedQuestion = (question.question?.text ?? question.question ?? '')
      .replace(/_+/g, '~')
      .split('#|#')[0]
      .split(' ')
      .map((part: any) => {
        const isBlank = part.startsWith('~');

        const questionPart: MultiChoiceQuestionPart = {
          isBlank,
          value: isBlank ? '' : part,
        };

        if (isBlank) {
          questionPart.correctAnswerPart = splittedAnswer[blankIndex] || '';
          blankIndex++;
        }

        return questionPart;
      });

    return splittedQuestion;
  },

  // Get question reply
  questionReply: (question: Question): string | undefined => {
    const splitQuestion = (question.question?.text ?? question.question ?? '').split('#|#');

    return splitQuestion[1];
  },
  //TODO: Need to understand does buildEndQuizData is needed
  // buildEndQuizData: async (quizQuestionResultData: QuizQuestionResult[]) => {},
  getStarsAmount: (score: number): number => {
    const lowestScore = 60;
    const mediumScore = 80;
    const highestScore = 100;

    let stars = 0;

    if (score >= highestScore) {
      stars = 3;
    } else if (score >= mediumScore) {
      stars = 2;
    } else if (score >= lowestScore) {
      stars = 1;
    } else {
      stars = 0;
    }

    return stars;
  },
  shouldDisplayActionModals: (gameQuestions: Question[]) => {
    return gameQuestions.length > 6;
  },

  isLastQuestion: (gameQuestions: Question[], currentQuestionIndex: number) => {
    return gameQuestions.length - 1 - currentQuestionIndex <= 0;
  },

  isLastActivity: (activityOrder: number | undefined, totalActivities: number | undefined) => {
    if (activityOrder === undefined || totalActivities === undefined) {
      return false;
    }

    return activityOrder === totalActivities;
  },

  getTopicCompletedCount: (
    progress: any,
    unitId: number | undefined,
    topicId: number | undefined
  ): number => {
    if (unitId == null || topicId == null) return 0;
    const topicProgress = getTopicProgress(progress, unitId, topicId);
    return typeof topicProgress?.completedActivitiesCount === 'number'
      ? topicProgress.completedActivitiesCount
      : 0;
  },

  isLastCompletedActivity: (
    topicId: number | undefined,
    activitiesInTopic: number | undefined,
    progress: any | undefined,
    unitId: number | undefined,
    isCompletedNow?: boolean | undefined,
    isCompletedBefore?: boolean | undefined,
    isSent?: boolean | undefined
  ): boolean => {
    const validTopic =
      topicId != null && typeof activitiesInTopic === 'number' && activitiesInTopic > 0;
    const validProgress = progress?.unit?.topicsOverviewPerUnit != null && unitId != null;
    if (!validTopic || !validProgress) {
      return false;
    }

    const topicProgress = getTopicProgress(progress, unitId, topicId);
    const completedCount =
      typeof topicProgress?.completedActivitiesCount === 'number'
        ? topicProgress.completedActivitiesCount
        : 0;

    if (completedCount >= activitiesInTopic) {
      return true;
    }
    if (isCompletedNow && !isCompletedBefore) {
      const countAfterThis = completedCount + 1;
      return isSent ? completedCount === activitiesInTopic : countAfterThis === activitiesInTopic;
    }
    return false;
  },

  calculateScore: (
    questionResults: QuizQuestionResult[],
    totalNumberOfQuestions: number = 0
  ): number => {
    const validAnswers = questionResults.filter(
      answer => answer.hasScore && answer.questionInputStatus !== 'NoInput'
    );
    const totalQuestionsWithScores = totalNumberOfQuestions || validAnswers.length;
    const correctAnswersCount = validAnswers.filter(answer => answer.isCorrect).length;

    return +commonUtils.calcPercentage(correctAnswersCount, totalQuestionsWithScores);
  },

  getTotalNumberOfQuestionsWithScore: (gameQuestions: Question[]): number => {
    let totalNumberOfQuestions = 0;

    if (gameQuestions?.length) {
      totalNumberOfQuestions = gameQuestions
        .map(q => {
          if (
            q.newData?.data?.metadata &&
            'hasScore' in q.newData.data.metadata &&
            !q.newData.data.metadata.hasScore
          ) {
            return false;
          }

          return true;
        })
        .filter(hasScore => hasScore).length;
    }

    return totalNumberOfQuestions;
  },

  isSkipped: (quizQuestionResultData: QuizQuestionResult[]) => {
    const hasNoInput = (data: QuizQuestionResult[]): boolean => {
      const filteredData = data.filter(obj => 'questionInputStatus' in obj);
      return (
        filteredData.length > 0 && filteredData.every(obj => obj.questionInputStatus === 'NoInput')
      );
    };

    const calculateMultichoiceScore = (data: QuizQuestionResult[]): number => {
      const multichoicePart = data.filter(
        obj => obj.gameType === NewGameTypes.NaturalConversation && !('questionInputStatus' in obj)
      );
      return GameUtils.calculateScore(multichoicePart);
    };

    const noInput = hasNoInput(quizQuestionResultData);

    const hasSpeakQuestions = quizQuestionResultData.some(
      obj => obj.gameType === NewGameTypes.Speak
    );
    const hasNaturalConversationQuestions = quizQuestionResultData.some(
      obj => obj.gameType === NewGameTypes.NaturalConversation
    );

    if (hasSpeakQuestions && !hasNaturalConversationQuestions) {
      return noInput;
    }

    if (hasNaturalConversationQuestions && noInput) {
      return true;
    }

    const multichoiceScore = calculateMultichoiceScore(quizQuestionResultData);
    return noInput && multichoiceScore === 0;
  },

  isFailed: (score: number = 0, threshold: number = 60): boolean => {
    return score < threshold;
  },

  getLastActivityFlags: (params: {
    unitId: number | undefined;
    topicId: number | undefined;
    activitiesInTopic: number | undefined;
    progress: any;
    unitTopics: Topic[] | undefined;
    units: Unit[];
    selectedUnitOrder: number | undefined;
    isCompletedNow: boolean;
    isCompletedBefore: boolean;
    isTeacher: boolean;
  }): {
    isLastActivityInTopic: boolean;
    isLastActivityInUnit: boolean;
    isLastActivityInLevel: boolean;
  } => {
    const {
      unitId,
      topicId,
      activitiesInTopic,
      progress,
      unitTopics,
      units,
      selectedUnitOrder,
      isCompletedNow,
      isCompletedBefore,
      isTeacher,
    } = params;

    const hasUnitAndTopic = unitId != null && topicId != null;
    const isLastActivityInTopic =
      !isTeacher &&
      hasUnitAndTopic &&
      GameUtils.isLastCompletedActivity(
        topicId,
        activitiesInTopic,
        progress,
        unitId,
        isCompletedNow,
        isCompletedBefore
      );

    const lastTopicInUnit =
      Array.isArray(unitTopics) && unitTopics.length > 0
        ? unitTopics[unitTopics.length - 1]
        : undefined;
    const isLastTopicInUnit =
      lastTopicInUnit != null && topicId != null && lastTopicInUnit.id === topicId;
    const isLastActivityInUnit = isLastTopicInUnit && isLastActivityInTopic;

    const unitsWithOrder = Array.isArray(units)
      ? units.filter(u => typeof u.order === 'number')
      : [];
    const maxUnitOrder =
      unitsWithOrder.length > 0 ? Math.max(...unitsWithOrder.map(u => u.order!)) : undefined;
    const isLastUnitInLevel =
      typeof selectedUnitOrder === 'number' &&
      maxUnitOrder !== undefined &&
      selectedUnitOrder === maxUnitOrder;
    const isLastActivityInLevel = isLastActivityInUnit && isLastUnitInLevel;

    return {
      isLastActivityInTopic,
      isLastActivityInUnit,
      isLastActivityInLevel,
    };
  },
};

export default GameUtils;
